import { CLINIC_NAME, CLINIC_SUBTITLE } from './utils/clinicConfig';
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import { SocketProvider } from './context/SocketContext';
import ProtectedRoute from './components/ProtectedRoute';

import LoginPage from './pages/LoginPage';
import NewTokenPage from './pages/NewTokenPage';
import QueuePage from './pages/QueuePage';
import AllPatientsPage from './pages/AllPatientsPage';
import BillingPage from './pages/BillingPage';
import DoctorDashboard from './pages/DoctorDashboard';
import ChangePasswordPage from './pages/ChangePasswordPage';
import LabPage from './pages/LabPage';
import CalendarPage from './pages/CalendarPage';
import PatientTrackerPage from './pages/PatientTrackerPage';
import PharmacyPage from './pages/PharmacyPage';
import TreatmentCatalogPage from './pages/TreatmentCatalogPage';
import BackupPage from './pages/BackupPage';
import ReschedulePage from './pages/ReschedulePage';
import NavSettingsPage from './pages/NavSettingsPage';
import SystemHealthPage from './pages/SystemHealthPage';
import ExpensesPage from './pages/ExpensesPage';
import SterilisationPage from './pages/SterilisationPage';
import PatientPortalPage from './pages/PatientPortalPage';

import './styles/global.css';
import './styles/components.css';
import './styles/layout.css';

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === 'doctor' ? '/doctor' : '/receptionist'} replace />;
}

export default function App() {
  React.useEffect(() => {
    document.title = `${CLINIC_NAME} — ${CLINIC_SUBTITLE}`;
  }, []);

  // Fix: scrolling the mouse wheel while it happens to be over a focused
  // number input silently changes that field's value in most browsers
  // (Chrome/Edge treat a focused <input type="number"> as a spinner control
  // under the cursor). Staff scrolling the page past an amount/discount/
  // quantity field they'd just clicked into were getting its value changed
  // without ever meaning to type anything. Rather than adding a handler to
  // every individual number input across the app, this blurs whichever
  // number input is focused the instant any wheel event fires anywhere —
  // the page then scrolls normally, and the field simply isn't focused
  // anymore to react to it. Covers every current AND future number input,
  // not just the ones existing today.
  React.useEffect(() => {
    function stopWheelChangingNumberInput(e) {
      const el = document.activeElement;
      if (el && el.tagName === 'INPUT' && el.type === 'number') {
        e.preventDefault(); // cancels the browser's native "spin the value" action for this event
        el.blur();          // so the next scroll tick behaves like normal page scrolling
      }
    }
    // Not passive: this listener needs to call preventDefault(), and only
    // does so in the rare case a number input is actually focused — every
    // other scroll on the page is completely unaffected.
    document.addEventListener('wheel', stopWheelChangingNumberInput, { passive: false });
    return () => document.removeEventListener('wheel', stopWheelChangingNumberInput);
  }, []);

  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <SocketProvider>
            <BrowserRouter>
              <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={<RootRedirect />} />

              {/* Doctor portal - requires authentication + doctor role */}
              <Route
                path="/doctor"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DoctorDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/queue"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <QueuePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/patients"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <AllPatientsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/billing"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <BillingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/settings"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <ChangePasswordPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/lab"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <LabPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/calendar"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <CalendarPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/tracker"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <PatientTrackerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/expenses"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <ExpensesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/pharmacy"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <PharmacyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/treatments"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <TreatmentCatalogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/create-token"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <NewTokenPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/backup"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <BackupPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/nav-settings"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <NavSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/system-health"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <SystemHealthPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/reschedule"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <ReschedulePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/sterilisation"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <SterilisationPage />
                  </ProtectedRoute>
                }
              />
              {/* Public patient portal — no auth required */}
              <Route path="/portal" element={<PatientPortalPage />} />

              {/* Receptionist portal - requires authentication + receptionist role */}
              <Route
                path="/receptionist"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <NewTokenPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/queue"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <QueuePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/patients"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <AllPatientsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/billing"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <BillingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/settings"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <ChangePasswordPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/lab"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <LabPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/calendar"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <CalendarPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/tracker"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <PatientTrackerPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/expenses"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <ExpensesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/pharmacy"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <PharmacyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/treatments"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <TreatmentCatalogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/reschedule"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <ReschedulePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/receptionist/sterilisation"
                element={
                  <ProtectedRoute allowedRoles={['receptionist']}>
                    <SterilisationPage />
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          </SocketProvider>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
